/**
 * Smart Field Mapper - Core Engine
 * =================================
 * Intelligent field mapping system for Clara360 with semantic understanding,
 * explainable AI, and cross-language support (German/English).
 * 
 * Features:
 * - Semantic field matching with synonym recognition
 * - Confidence scoring with explainable reasoning
 * - Learning from user feedback
 * - Support for PDF, Excel, CSV, DATEV formats
 * - Visual mapping UI integration
 * 
 * @author Clara360 AI Team
 * @version 1.0.0
 */

import Fuse from 'fuse.js';

class SmartFieldMapper {
  constructor(schemaConfig) {
    this.schema = schemaConfig;
    this.mappingHistory = [];
    this.userFeedback = [];
    this.confidenceThreshold = 0.70; // 70% minimum confidence
    
    // Initialize semantic layers
    this.initializeSemanticLayers();
  }

  /**
   * Initialize semantic layers with field definitions and synonyms
   */
  initializeSemanticLayers() {
    this.semanticLayers = {
      // Owner/Landlord fields
      owner: {
        fields: ['name', 'email', 'phone', 'mobile', 'company_name', 'tax_id'],
        germanSynonyms: {
          'name': ['Eigentümer', 'Vermieter', 'Besitzer', 'Name', 'Eigentümername'],
          'email': ['E-Mail', 'Email', 'Mailadresse', 'Kontakt-Email'],
          'phone': ['Telefon', 'Tel.', 'Telefonnummer', 'Festnetz'],
          'mobile': ['Mobil', 'Handy', 'Mobilnummer', 'Handynummer'],
          'company_name': ['Firma', 'Firmenname', 'Unternehmen', 'Gesellschaft'],
          'tax_id': ['Steuernummer', 'USt-ID', 'Steuer-ID', 'Steuernr.']
        },
        englishSynonyms: {
          'name': ['Owner', 'Landlord', 'Owner Name', 'Proprietor'],
          'email': ['Email', 'E-Mail', 'Contact Email', 'Email Address'],
          'phone': ['Phone', 'Telephone', 'Phone Number', 'Tel'],
          'mobile': ['Mobile', 'Cell', 'Mobile Phone', 'Cell Phone'],
          'company_name': ['Company', 'Company Name', 'Business Name'],
          'tax_id': ['Tax ID', 'Tax Number', 'VAT ID']
        }
      },

      // Property fields
      property: {
        fields: ['property_name', 'address', 'street', 'city', 'postal_code', 'property_type', 'year_built'],
        germanSynonyms: {
          'property_name': ['Objektname', 'Immobilienname', 'Gebäudename', 'Objektbezeichnung', 'Immobilie'],
          'address': ['Adresse', 'Anschrift', 'Straße', 'Standort', 'Objektadresse'],
          'street': ['Straße', 'Str.', 'Straßenname'],
          'city': ['Stadt', 'Ort', 'Gemeinde', 'Ortschaft'],
          'postal_code': ['PLZ', 'Postleitzahl', 'Postcode'],
          'property_type': ['Objektart', 'Immobilientyp', 'Gebäudeart', 'Art'],
          'year_built': ['Baujahr', 'Baujahr Gebäude', 'Errichtungsjahr']
        },
        englishSynonyms: {
          'property_name': ['Property', 'Property Name', 'Building Name', 'Object'],
          'address': ['Address', 'Street Address', 'Location'],
          'street': ['Street', 'Street Name', 'Road'],
          'city': ['City', 'Town', 'Municipality'],
          'postal_code': ['ZIP', 'Postal Code', 'ZIP Code', 'Postcode'],
          'property_type': ['Property Type', 'Building Type', 'Type'],
          'year_built': ['Year Built', 'Construction Year', 'Built Year']
        }
      },

      // Unit/Apartment fields
      unit: {
        fields: ['unit_identifier', 'size_sqm', 'rooms', 'floor', 'monthly_rent', 'cold_rent', 'warm_rent', 'utilities_cost', 'status'],
        germanSynonyms: {
          'unit_identifier': ['Wohnungsnummer', 'Einheit', 'Apartment', 'Whg.', 'Wohnung', 'Einheitsnummer'],
          'size_sqm': ['Größe', 'Fläche', 'Quadratmeter', 'qm', 'm²', 'Wohnfläche'],
          'rooms': ['Zimmer', 'Räume', 'Zimmeranzahl', 'Anzahl Zimmer'],
          'floor': ['Etage', 'Stockwerk', 'Geschoss', 'Stock'],
          'monthly_rent': ['Miete', 'Monatsmiete', 'Gesamtmiete'],
          'cold_rent': ['Kaltmiete', 'Nettomiete', 'Grundmiete'],
          'warm_rent': ['Warmmiete', 'Bruttomiete', 'Gesamtmiete'],
          'utilities_cost': ['Nebenkosten', 'NK', 'Betriebskosten', 'Umlagen'],
          'status': ['Status', 'Zustand', 'Vermietungsstatus']
        },
        englishSynonyms: {
          'unit_identifier': ['Unit Number', 'Apartment Number', 'Unit ID', 'Apt'],
          'size_sqm': ['Size', 'Area', 'Square Meters', 'sqm', 'Living Space'],
          'rooms': ['Rooms', 'Room Count', 'Number of Rooms', 'Bedrooms'],
          'floor': ['Floor', 'Level', 'Story'],
          'monthly_rent': ['Rent', 'Monthly Rent', 'Total Rent'],
          'cold_rent': ['Base Rent', 'Net Rent', 'Cold Rent'],
          'warm_rent': ['Gross Rent', 'Warm Rent', 'Total Rent'],
          'utilities_cost': ['Utilities', 'Utility Costs', 'Service Charges'],
          'status': ['Status', 'Rental Status', 'Occupancy Status']
        }
      },

      // Tenant fields
      tenant: {
        fields: ['name', 'email', 'phone', 'lease_start', 'lease_end', 'deposit', 'payment_method', 'iban'],
        germanSynonyms: {
          'name': ['Mieter', 'Mietername', 'Bewohner', 'Pächter', 'Name'],
          'email': ['E-Mail', 'Email', 'Mailadresse', 'Kontakt-Email'],
          'phone': ['Telefon', 'Tel.', 'Telefonnummer', 'Mobil', 'Handy'],
          'lease_start': ['Mietbeginn', 'Einzugsdatum', 'Vertragsbeginn', 'Start', 'Beginn'],
          'lease_end': ['Mietende', 'Auszugsdatum', 'Vertragsende', 'Ende', 'Kündigungsdatum'],
          'deposit': ['Kaution', 'Sicherheit', 'Mietkaution', 'Sicherheitsleistung'],
          'payment_method': ['Zahlungsart', 'Zahlungsmethode', 'Zahlungsweise'],
          'iban': ['IBAN', 'Bankverbindung', 'Kontonummer', 'Konto']
        },
        englishSynonyms: {
          'name': ['Tenant', 'Tenant Name', 'Renter', 'Lessee'],
          'email': ['Email', 'E-Mail', 'Contact Email'],
          'phone': ['Phone', 'Telephone', 'Mobile', 'Cell'],
          'lease_start': ['Start Date', 'Move-in Date', 'Lease Begin', 'Commencement'],
          'lease_end': ['End Date', 'Move-out Date', 'Lease End', 'Expiry'],
          'deposit': ['Deposit', 'Security Deposit', 'Bond'],
          'payment_method': ['Payment Method', 'Payment Type'],
          'iban': ['IBAN', 'Bank Account', 'Account Number']
        }
      }
    };
  }

  /**
   * Map source fields to target schema with confidence scoring
   * @param {Array} sourceFields - Fields from uploaded document
   * @param {String} targetTable - Target database table (owners, properties, units, tenants)
   * @param {String} language - Source language ('de' or 'en')
   * @returns {Object} Mapping results with confidence scores
   */
  mapFields(sourceFields, targetTable, language = 'de') {
    const mappings = [];
    const unmappedFields = [];
    
    // Get semantic layer for target table
    const semanticLayer = this.semanticLayers[targetTable];
    if (!semanticLayer) {
      throw new Error(`Unknown target table: ${targetTable}`);
    }

    // Process each source field
    for (const sourceField of sourceFields) {
      const mapping = this.findBestMatch(
        sourceField,
        semanticLayer,
        language
      );

      if (mapping.confidence >= this.confidenceThreshold) {
        mappings.push(mapping);
      } else {
        unmappedFields.push({
          sourceField,
          reason: 'Low confidence',
          confidence: mapping.confidence,
          suggestions: mapping.suggestions
        });
      }
    }

    // Calculate overall mapping quality
    const mappingQuality = this.calculateMappingQuality(mappings, sourceFields.length);

    return {
      mappings,
      unmappedFields,
      quality: mappingQuality,
      timestamp: new Date().toISOString(),
      targetTable,
      language
    };
  }

  /**
   * Find best matching target field for a source field
   * @param {String} sourceField - Source field name
   * @param {Object} semanticLayer - Semantic layer for target table
   * @param {String} language - Source language
   * @returns {Object} Best match with confidence and reasoning
   */
  findBestMatch(sourceField, semanticLayer, language) {
    const synonymKey = language === 'de' ? 'germanSynonyms' : 'englishSynonyms';
    const synonyms = semanticLayer[synonymKey];
    
    let bestMatch = null;
    let bestScore = 0;
    let matchReason = '';
    const suggestions = [];

    // Normalize source field for comparison
    const normalizedSource = this.normalizeFieldName(sourceField);

    // Check each target field
    for (const targetField of semanticLayer.fields) {
      const score = this.calculateSimilarity(
        normalizedSource,
        targetField,
        synonyms[targetField] || []
      );

      suggestions.push({
        targetField,
        confidence: score.confidence,
        reason: score.reason
      });

      if (score.confidence > bestScore) {
        bestScore = score.confidence;
        bestMatch = targetField;
        matchReason = score.reason;
      }
    }

    // Sort suggestions by confidence
    suggestions.sort((a, b) => b.confidence - a.confidence);

    return {
      sourceField,
      targetField: bestMatch,
      confidence: bestScore,
      reason: matchReason,
      suggestions: suggestions.slice(0, 3), // Top 3 suggestions
      requiresReview: bestScore < 0.85
    };
  }

  /**
   * Calculate similarity between source field and target field with synonyms
   * @param {String} sourceField - Normalized source field
   * @param {String} targetField - Target field name
   * @param {Array} synonyms - List of synonyms for target field
   * @returns {Object} Similarity score and reasoning
   */
  calculateSimilarity(sourceField, targetField, synonyms) {
    // Exact match
    if (sourceField === targetField.toLowerCase()) {
      return {
        confidence: 1.0,
        reason: 'Exact field name match'
      };
    }

    // Check synonyms
    const normalizedSynonyms = synonyms.map(s => this.normalizeFieldName(s));
    
    // Exact synonym match
    const exactSynonymMatch = normalizedSynonyms.indexOf(sourceField);
    if (exactSynonymMatch !== -1) {
      return {
        confidence: 0.95,
        reason: `Exact synonym match: "${synonyms[exactSynonymMatch]}"`
      };
    }

    // Fuzzy matching using Levenshtein distance
    const allCandidates = [targetField, ...synonyms].map(c => this.normalizeFieldName(c));
    
    let bestFuzzyScore = 0;
    let bestFuzzyMatch = '';
    
    for (let i = 0; i < allCandidates.length; i++) {
      const candidate = allCandidates[i];
      const similarity = this.levenshteinSimilarity(sourceField, candidate);
      
      if (similarity > bestFuzzyScore) {
        bestFuzzyScore = similarity;
        bestFuzzyMatch = i === 0 ? targetField : synonyms[i - 1];
      }
    }

    // Partial match (contains)
    if (sourceField.includes(targetField.toLowerCase()) || 
        targetField.toLowerCase().includes(sourceField)) {
      return {
        confidence: Math.max(0.75, bestFuzzyScore),
        reason: `Partial match with "${targetField}"`
      };
    }

    // Fuzzy match
    if (bestFuzzyScore > 0.6) {
      return {
        confidence: bestFuzzyScore,
        reason: `Fuzzy match with "${bestFuzzyMatch}" (${Math.round(bestFuzzyScore * 100)}% similar)`
      };
    }

    return {
      confidence: bestFuzzyScore,
      reason: 'No strong match found'
    };
  }

  /**
   * Normalize field name for comparison
   */
  normalizeFieldName(fieldName) {
    return fieldName
      .toLowerCase()
      .trim()
      .replace(/[_\-\s]+/g, '')  // Remove separators
      .replace(/[äöüß]/g, (char) => {
        const map = { 'ä': 'a', 'ö': 'o', 'ü': 'u', 'ß': 'ss' };
        return map[char] || char;
      });
  }

  /**
   * Calculate Levenshtein similarity (0-1 scale)
   */
  levenshteinSimilarity(str1, str2) {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    
    if (longer.length === 0) return 1.0;
    
    const distance = this.levenshteinDistance(longer, shorter);
    return (longer.length - distance) / longer.length;
  }

  /**
   * Calculate Levenshtein distance
   */
  levenshteinDistance(str1, str2) {
    const matrix = [];

    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }

    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }

    return matrix[str2.length][str1.length];
  }

  /**
   * Calculate overall mapping quality
   */
  calculateMappingQuality(mappings, totalFields) {
    if (totalFields === 0) return 0;

    const mappedCount = mappings.length;
    const avgConfidence = mappings.reduce((sum, m) => sum + m.confidence, 0) / mappedCount || 0;
    const coverageRatio = mappedCount / totalFields;

    return {
      score: (avgConfidence * 0.6 + coverageRatio * 0.4), // Weighted score
      avgConfidence,
      coverageRatio,
      mappedFields: mappedCount,
      totalFields,
      grade: this.getQualityGrade(avgConfidence * 0.6 + coverageRatio * 0.4)
    };
  }

  /**
   * Get quality grade
   */
  getQualityGrade(score) {
    if (score >= 0.90) return 'Excellent';
    if (score >= 0.80) return 'Good';
    if (score >= 0.70) return 'Fair';
    return 'Poor';
  }

  /**
   * Learn from user feedback
   * @param {Object} feedback - User correction/confirmation
   */
  learnFromFeedback(feedback) {
    this.userFeedback.push({
      ...feedback,
      timestamp: new Date().toISOString()
    });

    // Update semantic layers based on feedback
    if (feedback.type === 'correction') {
      this.updateSemanticLayer(
        feedback.sourceField,
        feedback.correctedTargetField,
        feedback.targetTable,
        feedback.language
      );
    }
  }

  /**
   * Update semantic layer with new synonym
   */
  updateSemanticLayer(sourceField, targetField, targetTable, language) {
    const semanticLayer = this.semanticLayers[targetTable];
    if (!semanticLayer) return;

    const synonymKey = language === 'de' ? 'germanSynonyms' : 'englishSynonyms';
    const synonyms = semanticLayer[synonymKey];

    if (synonyms[targetField] && !synonyms[targetField].includes(sourceField)) {
      synonyms[targetField].push(sourceField);
      console.log(`✅ Learned new synonym: "${sourceField}" → "${targetField}"`);
    }
  }

  /**
   * Export mapping configuration for review
   */
  exportMappingConfig() {
    return {
      semanticLayers: this.semanticLayers,
      mappingHistory: this.mappingHistory,
      userFeedback: this.userFeedback,
      confidenceThreshold: this.confidenceThreshold,
      version: '1.0.0',
      exportedAt: new Date().toISOString()
    };
  }
}

export default SmartFieldMapper;

